/**
 * Phase 261k — Per-event-kind notification preferences.
 *
 * Sits inside PushNotificationsCard once the user has push enabled.  Six
 * toggles, one per domain event (chat, fees, attendance, progress cards,
 * new invoices, broadcasts).  Optimistic UI — flips the local state first,
 * then persists; reverts on backend failure with a toast.
 *
 * Honest UX: never pretends a toggle saved when the network call fails.
 */
import { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

// Event-kind metadata.  Keys MUST match `ALL_EVENT_KINDS` exported from
// the backend `services/push_notifications.py`.
const EVENT_META = [
  { key: 'chat_message', label: 'Direct messages', help: 'Lockscreen ping for new chat messages.' },
  { key: 'fee_reminder', label: 'Fee reminders', help: 'When your academy resends a pending-fee reminder.' },
  { key: 'invoice_new', label: 'New invoices', help: 'When a new fee bill is raised on your account.' },
  { key: 'attendance', label: 'Attendance updates', help: 'When your coach marks today\'s attendance.' },
  { key: 'progress_card', label: 'Progress cards', help: 'When a coach publishes a new progress card.' },
  { key: 'broadcast', label: 'Academy announcements', help: 'Broadcast alerts from your academy.' },
];

export default function PushPreferencesPanel() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/push/preferences');
        if (!cancelled) setPrefs(data?.prefs || {});
      } catch (_) {
        if (!cancelled) setPrefs({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (key, next) => {
    const previous = prefs?.[key];
    setPrefs((p) => ({ ...(p || {}), [key]: next }));
    setSavingKey(key);
    try {
      const { data } = await api.put('/push/preferences', { prefs: { [key]: next } });
      setPrefs(data?.prefs || {});
    } catch (err) {
      // Revert + surface the failure honestly.
      setPrefs((p) => ({ ...(p || {}), [key]: previous }));
      toast.error('Could not save your notification preference. Please try again.');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="push-prefs-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading preferences…
      </div>
    );
  }

  return (
    <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-800" data-testid="push-prefs-panel">
      <div className="text-xs font-medium text-slate-700 dark:text-slate-300">
        What should we ping you about?
      </div>
      <div className="space-y-2">
        {EVENT_META.map(({ key, label, help }) => {
          const enabled = prefs?.[key] !== false;  // missing → default ON
          const busy = savingKey === key;
          return (
            <div
              key={key}
              className="flex items-start justify-between gap-3 py-1.5"
              data-testid={`push-prefs-row-${key}`}
            >
              <div className="space-y-0.5 flex-1 min-w-0">
                <Label htmlFor={`push-prefs-${key}`} className="text-xs font-medium cursor-pointer">
                  {label}
                </Label>
                <p className="text-[11px] text-muted-foreground leading-snug">{help}</p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                <Switch
                  id={`push-prefs-${key}`}
                  checked={enabled}
                  onCheckedChange={(v) => toggle(key, v)}
                  disabled={busy}
                  data-testid={`push-prefs-toggle-${key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
